import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import * as moment from 'moment';
import { AppSettings } from 'src/app/core/appsettings';
import { isCancelledPtBookingAction, PTBookingAction, Session } from 'src/app/core/models/trainer.models';
import { ToastService } from 'src/app/core/services/toast-service';

@Component({
  selector: 'app-list-sessions',
  templateUrl: './list-sessions.component.html'
})
export class ListSessionsComponent implements OnInit, OnChanges {

  // Best Selling data
  @Input() ListSessions: Array<Session> | undefined;

  /** Cached — `groupedRows()` was invoked every change detection via `*ngFor`. */
  groupedRowsCache: Array<{ item: any; members: any[]; memberCount: number }> = [];
  @Input() scheduledText: string = 'Scheduled';
  @Input() scheduledClass: string = 'btn-soft-success';
  @Input() cancelledText: string = 'Cancelled';
  @Input() cancelledClass: string = 'btn-soft-danger'
  @Output() clickRow = new EventEmitter<any>();

  constructor(private toast: ToastService) { }

  ngOnInit(): void {
    this.rebuildGroupedRows();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['ListSessions']) {
      this.rebuildGroupedRows();
    }
  }

  private rebuildGroupedRows(): void {
    this.groupedRowsCache = this.computeGroupedRows(this.ListSessions);
  }

  private static readonly GROUP_TYPE = 2;

  private bookingIdentity(item: any): string {
    const bookingId =
      item?.BookingId ?? item?.bookingId ??
      item?.GroupSessionId ?? item?.groupSessionId ??
      item?.SessionGroupId ?? item?.sessionGroupId ??
      item?.SlotSessionId ?? item?.slotSessionId ??
      item?.SlotId ?? item?.slotId;
    if (bookingId != null && bookingId !== '') {
      return `bid:${bookingId}`;
    }
    return `slot:${item?.TrainerId ?? ''}|${item?.StartDate ?? ''}|${item?.StartTime ?? ''}|${item?.Duration ?? ''}|${item?.Type ?? ''}|${item?.Action ?? ''}`;
  }

  private computeGroupedRows(list: Array<Session> | undefined): Array<{ item: any; members: any[]; memberCount: number }> {
    const src = list ?? [];
    const rows: Array<{ item: any; members: any[]; memberCount: number }> = [];
    const groupByKey = new Map<string, { item: any; members: any[]; memberCount: number }>();

    for (const item of src) {
      if (item?.Type !== ListSessionsComponent.GROUP_TYPE) {
        rows.push({ item, members: item?.Member ? [item.Member] : [], memberCount: item?.Member ? 1 : 0 });
        continue;
      }

      const key = this.bookingIdentity(item);
      const existing = groupByKey.get(key);
      if (!existing) {
        const member = item?.Member;
        const initialMembers = member ? [member] : [];
        const row = { item, members: initialMembers, memberCount: initialMembers.length };
        groupByKey.set(key, row);
        rows.push(row);
        continue;
      }

      const member = item?.Member;
      const memberId = member?.Id ?? item?.MemberId;
      const duplicate = existing.members.some(m => (m?.Id ?? null) === (memberId ?? null));
      if (!duplicate && member) {
        existing.members.push(member);
      }
      existing.memberCount = Math.max(existing.memberCount, existing.members.length);
    }

    return rows;
  }

  /** Arrow fn so `*ngFor` trackBy keeps component `this` (iterable differ calls the fn without binding). */
  trackGroupedRow = (_index: number, row: { item: any; members: any[]; memberCount: number }): string =>
    this.bookingIdentity(row.item);

  memberLabel(row: { item: any; members: any[]; memberCount: number }): string {
    const firstName = row.members?.[0]?.Person?.FullName || row.item?.Member?.Person?.FullName || 'Member';
    const extra = (row.memberCount || 0) - 1;
    return extra > 0 ? `${firstName} +${extra}` : firstName;
  }

  memberAvatar(row: { item: any; members: any[]; memberCount: number }): string {
    return row.members?.[0]?.Avatar?.UploadedUrl || row.item?.Member?.Avatar?.UploadedUrl || '';
  }

  getType(type: number) {
    switch (type) {
      case 2:
        return 'Group';
      default:
        return 'Private';
    }
  }

  getStartDate(startDate: string) {
    if (!startDate)
      return '';
    return moment(Date.parse(startDate)).format("dddd, MMMM D");
  }

  getStartTime(startTime: string) {
    if (!startTime)
      return '';
    return moment(Date.parse('2022-01-01T' + startTime)).format("LT");
  }

  getEndTime(endTime: string) {
    if (!endTime)
      return '';
    return moment(Date.parse('2022-01-01T' + endTime)).format("LT");
  }

  getDuration(data: { Duration?: number; duration?: number } | number | undefined): string {
    const mins = typeof data === 'object' && data != null
      ? (data.Duration ?? data.duration)
      : data;
    if (mins == null) return '—';
    return `${mins} min`;
  }

  /** Status `<td>` passes `$event` so `stopPropagation` avoids double fire from `<tr (click)>`. */
  onClickRow(item: any, event?: Event): void {
    if (this.isCancelled(item)) {
      return;
    }
    if (this.scheduledText === 'Completed' && !AppSettings.isPastSessionEditable(item)) {
      this.toast.showDanger(
        'Past sessions can only be edited within 72 hours of the scheduled start time.'
      );
      return;
    }
    event?.stopPropagation();
    this.clickRow.emit(item);
  }

  isCancelled(item: { Action?: number } | null | undefined): boolean {
    return isCancelledPtBookingAction(item?.Action);
  }

  statusText(status: number) {
    if (status === PTBookingAction.LateCancel) return 'Late Cancel';
    if (status === PTBookingAction.Cancel) return this.cancelledText;
    return this.scheduledText;
  }

  statusClass(status: number) {
    return `btn ${isCancelledPtBookingAction(status) ? this.cancelledClass : this.scheduledClass} rounded-pill`;
  }

  isEditable(item: any): boolean {
    if (this.isCancelled(item)) {
      return false;
    }
    if (this.scheduledText === 'Completed' && !AppSettings.isPastSessionEditable(item)) {
      return false;
    }
    return true;
  }

}
